import { useMemo, useContext } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ToastContext } from 'contexts';

/**
 * A wrapper around `useInfiniteQuery` that handles basic pagination logic.
 *
 * This hook heavily depends on the server returning `x-total-count` in the headers
 * in order to properly calculate the next page params.
 * @param {object} descriptor The query descriptor.
 * @param {Array<string>} descriptor.queryKey The query key.
 * @param {({ page }) => Promise<unknown>} descriptor.fetch The callback that is required to fetch data from the server.
 * @param {object} descriptor.options The options that can be passed to `useInfiniteQuery`.
 * @returns All fields from `useInfiniteQuery` plus a flattened array of data and the total count.
 */
const useBaseInfiniteQuery = ({ queryKey, fetch, options } = {}) => {
  const { t } = useTranslation();
  const { triggerToast } = useContext(ToastContext);

  const query = useInfiniteQuery(
    queryKey,
    ({ pageParam: page = 1 }) =>
      fetch({ page }).then(resp => ({
        total: parseInt(resp.headers?.['x-total-count'] ?? 0, 10),
        data: resp.data,
      })),
    {
      getNextPageParam: (lastPage, allPages) => {
        const totalFetched = allPages.reduce(
          (totalLength, page) => totalLength + page.data.length,
          0
        );
        return totalFetched < lastPage?.total ? allPages.length + 1 : undefined;
      },
      onError: () => triggerToast({ message: t('generic_error') }),
      keepPreviousData: true,
      ...options,
    }
  );

  const dataFlattened = useMemo(
    () => query.data?.pages.flatMap(page => page.data) ?? [],
    [query.data]
  );

  const total = useMemo(() => {
    const lastPage = query.data?.pages[query.data?.pages.length - 1];
    return lastPage?.total ?? 0;
  }, [query.data]);

  return { ...query, dataFlattened, total };
};

export default useBaseInfiniteQuery;
